
import { useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  Container,
  TextField,
} from "@mui/material";
import {
  EmojiObjects,
  Speed,
  CheckCircle,
  SolarPower,
  Bolt,
  Layers,
  Science,
  Calculate,
} from "@mui/icons-material";
import Particles from "react-tsparticles";

const COLORS = {
  blue: "#0b3c8c",
  darkBlue: "#08264d",
  yellow: "#ffd600",
  light: "#f5f7fa",
  text: "#263238",
};

const ASSUMPTIONS = {
  panelArea: 2.2, // m² на панел
  minPanelsPerKWp: 1.43,
  maxPanelsPerKWp: 2.5,
  minSolutionMlPerM2: 105,
  maxSolutionMlPerM2: 206,
  dilutionParts: 51, // 1 част концентрат + 50 части вода
  concentratePricePerLiter: 22.22,
  professionalPrice: 6400,
  professionalPowerKWp: 1500,
};

const formatNumber = (value, maximumFractionDigits = 2) =>
  new Intl.NumberFormat("bg-BG", {
    maximumFractionDigits,
  }).format(value);

const formatEuro = (value) =>
  `${formatNumber(value)} €`;

function InfoCard({
  value,
  label,
  description,
  yellow = false,
  icon,
}) {
  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2.5, md: 3 },
        height: "100%",
        minHeight: 160,
        borderRadius: 3,
        textAlign: "center",
        color: yellow ? COLORS.darkBlue : "#fff",
        background: yellow
          ? COLORS.yellow
          : `linear-gradient(145deg, ${COLORS.blue}, ${COLORS.darkBlue})`,
        border: yellow
          ? "1px solid #e7c400"
          : "1px solid rgba(255,255,255,0.12)",
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 8,
        },
      }}
    >
      <Box sx={{ mb: 1, opacity: 0.9 }}>{icon}</Box>

      <Typography
        sx={{
          fontSize: { xs: "1.45rem", md: "1.8rem" },
          fontWeight: 800,
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </Typography>

      <Typography fontWeight={700} sx={{ mt: 1 }}>
        {label}
      </Typography>

      {description && (
        <Typography
          variant="body2"
          sx={{ mt: 1, opacity: 0.85 }}
        >
          {description}
        </Typography>
      )}
    </Paper>
  );
}

function BenefitCard({ icon, title, description }) {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 3,
        textAlign: "center",
        border: "1px solid #e6ebf2",
        transition: "transform 0.25s ease",
        "&:hover": { transform: "translateY(-4px)" },
      }}
    >
      <Box sx={{ color: COLORS.blue, mb: 2 }}>{icon}</Box>
      <Typography fontWeight={800} mb={1}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Paper>
  );
}

export default function SolarROILandingFull() {
  const [powerInput, setPowerInput] = useState("10");
  const calculatorRef = useRef(null);

  const power = Number(powerInput);
  const validPower =
    powerInput.trim() !== "" &&
    Number.isFinite(power) &&
    power > 0 &&
    power <= 1000000;

  const results = useMemo(() => {
    if (!validPower) return null;

    const {
      panelArea,
      minPanelsPerKWp,
      maxPanelsPerKWp,
      minSolutionMlPerM2,
      maxSolutionMlPerM2,
      dilutionParts,
      concentratePricePerLiter,
      professionalPrice,
      professionalPowerKWp,
    } = ASSUMPTIONS;

    const panelCountMin = Math.ceil(power * minPanelsPerKWp);
    const panelCountMax = Math.floor(power * maxPanelsPerKWp);

    const totalAreaMin = panelCountMin * panelArea;
    const totalAreaMax = panelCountMax * panelArea;

    const solutionMinLiters =
      (totalAreaMin * minSolutionMlPerM2) / 1000;

    const solutionMaxLiters =
      (totalAreaMax * maxSolutionMlPerM2) / 1000;

    const concentrateMinLiters =
      solutionMinLiters / dilutionParts;

    const concentrateMaxLiters =
      solutionMaxLiters / dilutionParts;

    const concentrateCostMin =
      concentrateMinLiters * concentratePricePerLiter;

    const concentrateCostMax =
      concentrateMaxLiters * concentratePricePerLiter;

    const professionalServiceCost =
      (power / professionalPowerKWp) * professionalPrice;

    return {
      panelCountMin,
      panelCountMax,
      totalAreaMin,
      totalAreaMax,
      solutionMinLiters,
      solutionMaxLiters,
      concentrateMinLiters,
      concentrateMaxLiters,
      concentrateCostMin,
      concentrateCostMax,
      professionalServiceCost,
      differenceMin:
        professionalServiceCost - concentrateCostMax,
      differenceMax:
        professionalServiceCost - concentrateCostMin,
    };
  }, [power, validPower]);

  const scrollToCalculator = () => {
    calculatorRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const benefits = [
    {
      icon: <EmojiObjects fontSize="large" />,
      title: "По-добър добив",
      description:
        "Редовната и правилно извършена поддръжка помага за отстраняването на замърсяванията по панелите.",
    },
    {
      icon: <Speed fontSize="large" />,
      title: "Планирана поддръжка",
      description:
        "Изберете подходящ режим на почистване според условията и замърсяването на вашата централа.",
    },
    {
      icon: <CheckCircle fontSize="large" />,
      title: "Контрол върху разходите",
      description:
        "Оценете количеството концентрат, необходимо за едно почистване.",
    },
    {
      icon: <Bolt fontSize="large" />,
      title: "По-лесно планиране",
      description:
        "Използвайте ориентировъчните резултати, за да планирате нужните материали.",
    },
  ];

  return (
    <Box sx={{ overflow: "hidden", color: COLORS.text }}>
      {/* HERO */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          py: { xs: 10, md: 15 },
          px: 2,
          textAlign: "center",
          color: "#fff",
          background:
            "linear-gradient(135deg, #08264d 0%, #0b3c8c 60%, #3476bd 100%)",
        }}
      >
        <Particles
          id="tsparticles-roi"
          options={{
            background: { color: "transparent" },
            fpsLimit: 45,
            interactivity: {
              events: {
                onHover: { enable: false },
                onClick: { enable: false },
              },
            },
            particles: {
              color: { value: COLORS.yellow },
              links: { enable: false },
              move: {
                enable: true,
                speed: 0.25,
                direction: "top",
                outModes: { default: "out" },
              },
              number: { value: 35 },
              opacity: { value: 0.25 },
              size: { value: { min: 1, max: 3 } },
            },
          }}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        />

        <SolarPower
          sx={{
            position: "absolute",
            top: 24,
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: { xs: 80, md: 120 },
            color: COLORS.yellow,
            opacity: 0.12,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="overline"
            sx={{
              color: COLORS.yellow,
              fontWeight: 800,
              letterSpacing: 2,
            }}
          >
            TC ENERGY · SOLAR CARE
          </Typography>

          <Typography
            component="h1"
            sx={{
              mt: 2,
              mb: 2,
              fontSize: { xs: "2rem", sm: "2.7rem", md: "3.4rem" },
              lineHeight: 1.15,
              fontWeight: 800,
              maxWidth: 950,
              mx: "auto",
            }}
          >
            Изчисли разхода за почистване със SWP
          </Typography>

          <Typography
            sx={{
              maxWidth: 760,
              mx: "auto",
              mb: 4,
              fontSize: { xs: "1rem", md: "1.2rem" },
              lineHeight: 1.8,
              color: "rgba(255,255,255,0.9)",
            }}
          >
            Въведи мощността на своята соларна централа и получи
            ориентировъчна оценка за необходимия препарат и
            професионалното почистване.
          </Typography>

          <Button
            onClick={scrollToCalculator}
            variant="contained"
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 800,
              color: COLORS.darkBlue,
              backgroundColor: COLORS.yellow,
              "&:hover": {
                backgroundColor: "#ffe45c",
                transform: "translateY(-2px)",
              },
            }}
          >
            Изчисли сега
          </Button>
        </Container>
      </Box>

      <Container
        maxWidth="lg"
        ref={calculatorRef}
        sx={{ py: { xs: 6, md: 9 } }}
      >
        {/* INPUT */}
        <Box sx={{ maxWidth: 720, mx: "auto", mb: 7 }}>
          <Typography
            component="h2"
            variant="h4"
            fontWeight={800}
            textAlign="center"
            sx={{ mb: 2, color: COLORS.darkBlue }}
          >
            Данни за соларната централа
          </Typography>

          <Typography
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            Въведи инсталираната мощност в kWp.
            Например 10, 100 или 1500.
          </Typography>

          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              borderTop: `5px solid ${COLORS.yellow}`,
            }}
          >
            <TextField
              fullWidth
              label="Инсталирана мощност"
              value={powerInput}
              onChange={(event) => {
                const value = event.target.value;
                if (/^\d*\.?\d*$/.test(value)) {
                  setPowerInput(value);
                }
              }}
              type="text"
              inputProps={{
                inputMode: "decimal",
                "aria-label": "Мощност на централата в kWp",
              }}
              error={!validPower}
              helperText={
                validPower
                  ? `Въведена мощност: ${formatNumber(power)} kWp`
                  : "Въведи число над 0 и не повече от 1 000 000 kWp."
              }
              InputProps={{
                endAdornment: (
                  <Typography
                    sx={{
                      color: COLORS.blue,
                      fontWeight: 800,
                      ml: 1,
                    }}
                  >
                    kWp
                  </Typography>
                ),
              }}
            />

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2 }}
            >
              1000 kWp = 1 MWp. Изчисленията са ориентировъчни
              и зависят от реалната конфигурация на централата.
            </Typography>
          </Paper>
        </Box>

        {/* RESULTS */}
        <Typography
          component="h2"
          variant="h4"
          fontWeight={800}
          textAlign="center"
          sx={{ mb: 4, color: COLORS.darkBlue }}
        >
          Резултати от изчислението
        </Typography>

        {!results ? (
          <Paper
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 3,
            }}
          >
            Въведи валидна мощност, за да видиш резултатите.
          </Paper>
        ) : (
          <>
            <Grid container spacing={3} sx={{ mb: 6 }}>
              <Grid item xs={12} sm={6} md={3}>
                <InfoCard
                  icon={<SolarPower fontSize="large" />}
                  value={`${formatNumber(results.panelCountMin, 0)}–${formatNumber(results.panelCountMax, 0)}`}
                  label="Ориентировъчен брой панели"
                  description="Според приетия диапазон панели за kWp"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <InfoCard
                  icon={<Layers fontSize="large" />}
                  value={`${formatNumber(results.totalAreaMin)}–${formatNumber(results.totalAreaMax)} m²`}
                  label="Обща площ"
                  description="При площ 2,2 m² на панел"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <InfoCard
                  icon={<Science fontSize="large" />}
                  value={`${formatNumber(results.concentrateMinLiters)}–${formatNumber(results.concentrateMaxLiters)} л`}
                  label="Solar Wash Protect"
                  description="За едно почистване при разреждане 1:50"
                  yellow
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <InfoCard
                  icon={<Calculate fontSize="large" />}
                  value={`${formatNumber(results.concentrateCostMin)}–${formatNumber(results.concentrateCostMax)} €`}
                  label="Цена на концентрата"
                  description="При цена 22,22 €/л"
                  yellow
                />
              </Grid>
            </Grid>

            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                background: COLORS.light,
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography
                component="h2"
                variant="h5"
                fontWeight={800}
                sx={{ color: COLORS.darkBlue, mb: 1 }}
              >
                Сравнение с професионално почистване
              </Typography>

              <Typography color="text.secondary" sx={{ mb: 4 }}>
                Базова цена: 4200 € за едно пълно почистване на
                централа от 1000 kWp с робот.
                За други мощности е приложена пропорционална оценка.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: "100%",
                      borderRadius: 3,
                      backgroundColor: "#fff",
                      border: "1px solid #dce4ef",
                    }}
                  >
                    <Typography color="text.secondary" gutterBottom>
                      Професионална услуга
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: "1.8rem", md: "2.2rem" },
                        fontWeight: 800,
                        color: COLORS.blue,
                      }}
                    >
                      {formatEuro(results.professionalServiceCost)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ориентировъчна цена за едно почистване
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: "100%",
                      borderRadius: 3,
                      backgroundColor: "#fff",
                      border: "1px solid #dce4ef",
                    }}
                  >
                    <Typography color="text.secondary" gutterBottom>
                      Само концентрат Solar Wash Protect 1:50
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: "1.8rem", md: "2.2rem" },
                        fontWeight: 800,
                        color: COLORS.blue,
                      }}
                    >
                      {formatNumber(results.concentrateCostMin)}–
                      {formatNumber(results.concentrateCostMax)} €
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Без труд, вода и оборудване
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 3,
                  p: 2.5,
                  borderRadius: 2,
                  backgroundColor: "#fff8d9",
                  border: "1px solid #f0df8c",
                }}
              >
                <Typography fontWeight={800} sx={{ mb: 1 }}>
                  Важно за правилното сравнение
                </Typography>
                <Typography variant="body2">
                  Разликата между цената на услугата и цената на
                  концентрата не е нетна икономия. При самостоятелно
                  почистване може да има допълнителни разходи за
                  персонал, вода, оборудване, транспорт и безопасност.
                  Те не са включени в тази оценка.
                </Typography>
              </Box>
            </Paper>
          </>
        )}

        {/* BENEFITS */}
        <Box sx={{ mt: { xs: 7, md: 10 }, textAlign: "center" }}>
          <Typography
            component="h2"
            variant="h4"
            fontWeight={800}
            sx={{ color: COLORS.darkBlue, mb: 2 }}
          >
            Защо е важна правилната поддръжка?
          </Typography>

          <Typography
            color="text.secondary"
            sx={{ maxWidth: 760, mx: "auto", mb: 5 }}
          >
            Редовната поддръжка и подходящият препарат помагат
            за планирането на разходите и грижата за соларните панели.
          </Typography>

          <Grid container spacing={3}>
            {benefits.map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.title}>
                <BenefitCard {...item} />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* DISCLAIMER */}
        <Box
          sx={{
            mt: 6,
            p: 2,
            borderLeft: `4px solid ${COLORS.yellow}`,
            backgroundColor: "#f8fafc",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Резултатите са ориентировъчни. Броят панели е изведен
            от зададен диапазон 1430–2500 панела за 1 MWp.
            Разходът на разтвор е изчислен при площ 2,2 m² на панел,
            разход 105–206 ml/m² и разреждане 1:50 с препарат Solar Wash Protect, при което
            1 част концентрат образува 51 части готов разтвор
            с 50 части вода. Реалният разход зависи от продукта,
            метода на нанасяне и конкретната централа.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
